import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { getPropertyProgress, type ProgressRow, type PropertyProgress } from "@/lib/api";
import { useLanguage } from "@/i18n";
import { formatNumber } from "@/lib/format";
import { Spinner } from "./ui";

/* Surveyed is the one measured quantity; "not started" is the remainder, so it
   is drawn as the neutral track of the same ring rather than a second hue. */
const SURVEYED = "#0096C7";
const NOT_STARTED = "#D4DAE2";

/**
 * Properties surveyed vs not started for one project: a pie (donut) of the
 * project total, plus a ward-wise / block-wise breakdown with counts.
 *
 * Results are cached per project, because the projects page switches project
 * on hover and would otherwise refetch on every pass of the pointer.
 */
export default function PropertyProgressPanel({
  projectId,
  caption,
}: {
  projectId: number;
  caption?: string;
}) {
  const { t, lang } = useLanguage();
  const tp = t.projectsPage;
  const cache = useRef<Record<number, PropertyProgress>>({});
  const [data, setData] = useState<PropertyProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState<"ward" | "block">("ward");

  useEffect(() => {
    const cached = cache.current[projectId];
    if (cached) {
      setData(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setData(null);
    getPropertyProgress(projectId)
      .then((d) => {
        cache.current[projectId] = d;
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setFailed(true);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const rows = useMemo(() => {
    const list = (view === "ward" ? data?.wards : data?.blocks) ?? [];
    // Areas with properties first (most first), empty ones after, by name.
    return [...list].sort(
      (a, b) =>
        b.surveyed + b.not_started - (a.surveyed + a.not_started) ||
        a.name.localeCompare(b.name, undefined, { numeric: true })
    );
  }, [data, view]);

  const total = data?.total ?? 0;
  const surveyed = data?.surveyed ?? 0;
  const notStarted = data?.not_started ?? 0;
  const pct = total > 0 ? Math.round((surveyed / total) * 100) : 0;

  return (
    <section className="min-h-[15rem] rounded-md bg-surface p-4 shadow-ring">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">
          {tp.propertyProgress}
        </p>
        <p className="text-xs text-ink-muted">
          {caption}
          {data ? ` · ${tp.propertiesTotal(total)}` : ""}
        </p>
      </div>

      {!data || data.total === 0 ? (
        <div className="flex items-center justify-center gap-2 py-10 text-xs text-ink-muted">
          {loading ? (
            <>
              <Spinner /> {tp.loadingProgress}
            </>
          ) : failed ? (
            <span className="text-status-text-critical">{tp.progressFailed}</span>
          ) : (
            tp.noProperties
          )}
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-[220px_minmax(0,1fr)]">
          {/* Pie: project total */}
          <div className="flex flex-col items-center gap-3">
            <Donut surveyed={surveyed} notStarted={notStarted} label={`${pct}%`} sub={tp.surveyed} />
            <ul className="w-full space-y-1 text-xs">
              <LegendRow color={SURVEYED} label={tp.surveyed} value={formatNumber(surveyed, lang)} pct={total ? (surveyed / total) * 100 : 0} />
              <LegendRow color={NOT_STARTED} label={tp.notStarted} value={formatNumber(notStarted, lang)} pct={total ? (notStarted / total) * 100 : 0} />
            </ul>
          </div>

          {/* Ward-wise / block-wise counts */}
          <div className="min-w-0">
            <div className="mb-2 inline-flex rounded-md bg-surface-sunken p-0.5 text-xs">
              {(["ward", "block"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  aria-pressed={view === v}
                  className={clsx(
                    "rounded px-2.5 py-1 transition",
                    view === v ? "bg-surface font-semibold text-ink shadow-ring" : "text-ink-muted hover:text-ink"
                  )}
                >
                  {v === "ward" ? tp.byWard : tp.byBlock}
                </button>
              ))}
            </div>
            {rows.length === 0 ? (
              <p className="py-6 text-center text-xs text-ink-muted">{tp.noProperties}</p>
            ) : (
              <div className="thin-scroll max-h-56 overflow-auto">
                <table className="w-full border-collapse text-left text-xs">
                  <thead className="sticky top-0 bg-surface text-2xs uppercase tracking-wider text-ink-muted">
                    <tr>
                      <th className="py-1.5 pr-3 font-semibold">{view === "ward" ? tp.colWard : tp.colBlock}</th>
                      <th className="py-1.5 pr-3 font-semibold">{tp.surveyed}</th>
                      <th className="py-1.5 pr-3 font-semibold">{tp.notStarted}</th>
                      <th className="w-[40%] py-1.5 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <BreakdownRow key={r.id} row={r} view={view} lang={lang} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Donut({
  surveyed,
  notStarted,
  label,
  sub,
}: {
  surveyed: number;
  notStarted: number;
  label: string;
  sub: string;
}) {
  const total = surveyed + notStarted;
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const share = total > 0 ? surveyed / total : 0;
  return (
    <svg viewBox="0 0 140 140" className="h-36 w-36" role="img" aria-label={`${label} ${sub}`}>
      <circle cx="70" cy="70" r={r} fill="none" stroke={NOT_STARTED} strokeWidth="18">
        <title>{`${notStarted} / ${total}`}</title>
      </circle>
      {share > 0 && (
        <circle
          cx="70"
          cy="70"
          r={r}
          fill="none"
          stroke={SURVEYED}
          strokeWidth="18"
          strokeDasharray={`${share * circumference} ${circumference}`}
          transform="rotate(-90 70 70)"
        >
          <title>{`${surveyed} / ${total}`}</title>
        </circle>
      )}
      <text x="70" y="68" textAnchor="middle" className="fill-ink text-[22px] font-semibold">
        {label}
      </text>
      <text x="70" y="88" textAnchor="middle" className="fill-ink-muted text-[11px]">
        {sub}
      </text>
    </svg>
  );
}

function LegendRow({ color, label, value, pct }: { color: string; label: string; value: string; pct: number }) {
  return (
    <li className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 flex-none rounded-sm" style={{ backgroundColor: color }} />
      <span className="text-ink-secondary">{label}</span>
      <span className="tabular ml-auto font-semibold text-ink">{value}</span>
      <span className="tabular w-10 text-right text-2xs text-ink-muted">{Math.round(pct)}%</span>
    </li>
  );
}

function BreakdownRow({ row, view, lang }: { row: ProgressRow; view: "ward" | "block"; lang: "en" | "mr" }) {
  const total = row.surveyed + row.not_started;
  const sub = view === "ward" ? row.zone_name : row.ward_name;
  return (
    <tr className="border-t border-line" title={`${row.surveyed} / ${total}`}>
      <td className="py-1.5 pr-3">
        <div className="font-medium text-ink">{row.name}</div>
        {sub && <div className="text-2xs text-ink-muted">{sub}</div>}
      </td>
      <td className="tabular py-1.5 pr-3 font-semibold text-ink">{formatNumber(row.surveyed, lang)}</td>
      <td className="tabular py-1.5 pr-3 text-ink-secondary">{formatNumber(row.not_started, lang)}</td>
      <td className="py-1.5">
        <div className="flex h-2 w-full gap-[2px] overflow-hidden rounded-full bg-surface-sunken">
          {row.surveyed > 0 && (
            <span className="h-full rounded-full" style={{ width: `${(row.surveyed / total) * 100}%`, backgroundColor: SURVEYED }} />
          )}
          {row.not_started > 0 && (
            <span className="h-full rounded-full" style={{ width: `${(row.not_started / total) * 100}%`, backgroundColor: NOT_STARTED }} />
          )}
        </div>
      </td>
    </tr>
  );
}
